import { minBy } from 'es-toolkit';

import { groupTransitively } from '@paragon/utilities';

import Point3D from './Point3D';
import { distanceEquals } from './units';

/**
 * Groups the source items transitively by distance equality. Useful for "pre-grouping" geometric elements before making
 * an expensive call to `groupTransitively`.
 */
export function groupTransitivelyByDistanceEquality<T>(source: readonly T[], getDistance: (item: T) => number) {
  if (source.length === 0) return [];

  const sorted = source.toSorted((item1, item2) => getDistance(item1) - getDistance(item2));

  const [firstItem, ...restItems] = sorted;
  const grouped = [[firstItem]];
  for (const item of restItems) {
    const lastGroup = grouped.at(-1)!;
    const lastItem = lastGroup.at(-1)!;
    if (distanceEquals(getDistance(lastItem), getDistance(item))) {
      lastGroup.push(item);
    } else {
      grouped.push([item]);
    }
  }

  return grouped;
}

export const groupPointsTransitively = (points: Point3D[]) =>
  groupTransitivelyByDistanceEquality(points, (point) => point.x).flatMap((group) =>
    groupTransitively(group, (point1, point2) => point1.equals(point2)),
  );

interface Segment<T> {
  readonly midpoint: { readonly x: number };
  equals(segment: T): boolean;
}

export const groupSegmentsTransitively = <T extends Segment<T>>(segments: readonly T[]) =>
  groupTransitivelyByDistanceEquality(segments, (segment) => segment.midpoint.x).flatMap((group) =>
    groupTransitively(group, (segment1, segment2) => segment1.equals(segment2)),
  );

/**
 * Returns the subset of `items` which map to the minimum distance calculated by `getDistance` (respecting tolerance),
 * sorted by mapped value. See the server's `MinimaFuzzy` for more details.
 */
export const minimaFuzzy = <T>(items: readonly T[], getDistance: (item: T) => number): T[] => {
  if (!items.length) return [];
  const withProjection = items.map((item) => ({ item, projection: getDistance(item) }));
  const minProjection = minBy(withProjection, ({ projection }) => projection)!.projection;
  return withProjection
    .filter(({ projection }) => distanceEquals(projection, minProjection))
    .toSorted((a, b) => a.projection - b.projection)
    .map(({ item }) => item);
};
